import React, { useEffect, useRef, useState, useCallback } from 'react';
import FindOverlay from './FindOverlay';
import { getEditorViews } from '../lib/cmRegistry';
import { setFindMatches, scrollFindMatchIntoView } from './MarkdownEditor';
import {
  getAgendaRows, publishAgendaFind, clearAgendaFind, scrollAgendaRowIntoView,
} from '../lib/agendaFind';

/**
 * FindBar — Walter 2026-06-16. Browser-style "find in page" for the list
 * text. Triggered by Ctrl/Cmd+F (Shift+Ctrl/Cmd+F = the cross-board results
 * search, SearchModal). Native browser find can't see inside the list
 * <textarea>s, so this is our own.
 *
 * Highlighting is done by FindOverlay (a transparent mirror layer over each
 * matching textarea) — ALL matches stay lit at once, current one stronger,
 * independent of focus (Walter: "I'm used to seeing all the hits highlighted
 * immediately, then toggling through them"). The find input keeps the
 * keyboard; stepping just moves which match is "current" and scrolls it into
 * view (we do NOT focus the textarea — that would steal the keyboard and
 * trip the editor's capture/lock side effects).
 *
 * Scope: a list open full-screen marks itself `[data-find-scope]` and becomes
 * the whole search — its tiles stay mounted behind the overlay and would
 * otherwise contribute hits nobody can see. With no such panel open, the scope
 * is the board grid: every rendered list-content textarea
 * (`[data-find-target]`) plus every mounted live-preview editor.
 */

// Measure the pixel y-offset of character `index` inside a textarea,
// accounting for WRAPPED lines (counting '\n's underestimates, which left the
// first match scrolled just below the fold — Walter 2026-06-20). Uses a hidden
// mirror div sized to the textarea's content column, same technique as
// FindOverlay. Returns pixels from the top of the content.
function measureMatchTop(el, index) {
  try {
    const cs = window.getComputedStyle(el);
    const div = document.createElement('div');
    const s = div.style;
    s.position = 'absolute';
    s.visibility = 'hidden';
    s.left = '-9999px';
    s.top = '0';
    s.boxSizing = 'border-box';
    s.width = `${el.clientWidth}px`;
    [
      'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
      'letterSpacing', 'lineHeight', 'textTransform', 'wordSpacing', 'textIndent',
      'whiteSpace', 'wordBreak', 'overflowWrap', 'tabSize',
      'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    ].forEach((k) => { s[k] = cs[k]; });
    if (!s.whiteSpace || s.whiteSpace === 'normal') s.whiteSpace = 'pre-wrap';
    div.textContent = (el.value || '').slice(0, index);
    const marker = document.createElement('span');
    marker.textContent = '​';
    div.appendChild(marker);
    document.body.appendChild(div);
    const top = marker.offsetTop;
    document.body.removeChild(div);
    return top;
  } catch {
    return null;
  }
}

// While a list is open full-screen, the board's tiles stay mounted behind the
// overlay — visible to any DOM query, unreadable to the user. Walter
// 2026-08-24: searching "test" in an open list reported 4 hits for a word that
// appears once on screen. When such a panel is present, it IS the search
// scope; otherwise the whole document is (the board grid, all tiles).
function findScopeRoot() {
  try {
    const scopes = document.querySelectorAll('[data-find-scope]');
    // Last one wins if several are somehow mounted — that's the topmost.
    return scopes.length ? scopes[scopes.length - 1] : document;
  } catch {
    return document;
  }
}

function collectMatches(query) {
  const matches = [];
  const scope = findScopeRoot();
  const q = (query || '').toLowerCase();
  if (!q) return matches;
  const tas = Array.from(scope.querySelectorAll('textarea[data-find-target]'));
  for (const ta of tas) {
    if (ta.offsetParent === null && ta.getClientRects().length === 0) continue; // hidden
    const text = (ta.value || '').toLowerCase();
    if (!text) continue;
    let i = text.indexOf(q);
    while (i !== -1) {
      matches.push({ el: ta, start: i, end: i + q.length });
      i = text.indexOf(q, i + q.length); // non-overlapping
    }
  }

  // Walter 2026-08-23: the live-preview editor too. Ctrl+F searched textareas
  // only, and CodeMirror isn't one — so search returned 0/0 in every list for
  // anyone on the beta editor, which is now both his devices.
  //
  // We search the DOCUMENT, not the rendering: CodeMirror hides the syntax
  // markers, so screen text and document text have different offsets and a hit
  // found in the rendering couldn't be mapped back to a position. A
  // consequence worth knowing — searching for text that straddles a hidden
  // marker DOES match here, and the caret lands on the raw text.
  //
  // These carry `view` instead of `el`. FindOverlay mirrors textareas to light
  // every hit at once and can't do that for CodeMirror, so a CM6 hit is shown
  // by SELECTING it in the editor (see scrollToMatch) — the current match is
  // visible, the rest are counted.
  for (const view of getEditorViews()) {
    if (scope !== document && !scope.contains(view.dom)) continue;
    let text;
    try { text = view.state.doc.toString().toLowerCase(); } catch { continue; }
    if (!text) continue;
    let i = text.indexOf(q);
    while (i !== -1) {
      matches.push({ view, start: i, end: i + q.length });
      i = text.indexOf(q, i + q.length);
    }
  }

  // The agenda panel (Walter 2026-08-23: "search doesn't work on this mode").
  // Its rows are plain text, so a hit is just a substring — the row paints its
  // own highlight (lib/agendaFind). Only rows the CURRENT FILTER TAB shows are
  // published, which is the v1 scope Walter picked: you search what you see.
  // ...but ONLY when the agenda is the panel you're looking at. Walter's QA
  // pass 2026-08-25: searching "milk" in a list reported 1/2 with a single
  // match on screen — the agenda was CLOSED, and its rows were still being
  // searched, so the phantom hit belonged to a reminder nobody could see. The
  // registry outlives a close in some paths; the scope is the reliable signal.
  const agendaIsScope = scope !== document && scope.getAttribute
    && scope.getAttribute('data-find-scope') === 'agenda';
  for (const row of (agendaIsScope ? getAgendaRows() : [])) {
    // Reminder text first, then its notes — so stepping reads top-to-bottom
    // the way the row does. Notes hits carry field:'notes'; the row opens
    // itself for them, since a collapsed note would hide what was counted.
    for (const field of ['text', 'notes']) {
      const text = (row[field] || '').toLowerCase();
      if (!text) continue;
      let i = text.indexOf(q);
      while (i !== -1) {
        matches.push({ agendaId: row.id, field, start: i, end: i + q.length });
        i = text.indexOf(q, i + q.length);
      }
    }
  }
  return matches;
}

const NO_MATCHES = [];

export default function FindBar({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [idx, setIdx] = useState(0);
  // Walter 2026-06-20: editing a list while find is open shifts the text, so
  // the cached match offsets stop lining up ("highlights below where I'm
  // typing get thrown off"). We suspend the overlay the moment the user types
  // into a list, and bring it back — recomputed against the new text — when
  // they click/focus the find box (or press a step key) again.
  const [suspended, setSuspended] = useState(false);
  const inputRef = useRef(null);

  // Scroll the current match into view WITHOUT focusing the textarea (the
  // overlay shows the highlight; focusing would steal the find input's
  // keyboard and run the editor's capture/edit-lock handlers).
  const scrollToMatch = useCallback((list, i) => {
    const m = list[i];
    if (!m) return;
    // CodeMirror hit — scroll it into view. The HIGHLIGHT is painted
    // separately, by the decoration effect below (paintCmMatches): moving the
    // selection here instead was the first cut and Walter couldn't see any of
    // it, because CodeMirror only draws a selection while the view has focus
    // and the find bar deliberately keeps the keyboard.
    if (m.view) {
      scrollFindMatchIntoView(m.view, m.start);
      return;
    }
    // Agenda row — the row paints the hit itself; just bring it on screen.
    if (m.agendaId) {
      scrollAgendaRowIntoView(m.agendaId);
      return;
    }
    const el = m.el;
    try {
      el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      // Accurate position (handles wrapped lines); fall back to the rough
      // newline-count estimate only if measuring fails.
      let top = measureMatchTop(el, m.start);
      if (top == null) {
        const line = (el.value || '').slice(0, m.start).split('\n').length - 1;
        const lh = parseFloat(window.getComputedStyle(el).lineHeight) || 18;
        top = line * lh;
      }
      // Sit the match ~a third down the visible area so there's context above.
      el.scrollTop = Math.max(0, top - el.clientHeight * 0.35);
    } catch { /* ignore */ }
  }, []);

  // Push the current hit list into each live-preview editor so it can draw
  // them. Every registered view is addressed, including ones with no hits, so
  // an editor that stops matching gets cleared rather than keeping stale
  // paint. Suspended (mid-edit) search clears everything.
  const paintCmMatches = useCallback((list, current) => {
    const byView = new Map();
    getEditorViews().forEach(v => byView.set(v, []));
    (list || []).forEach((m, i) => {
      if (!m.view) return;
      if (!byView.has(m.view)) byView.set(m.view, []);
      byView.get(m.view).push({ from: m.start, to: m.end, globalIndex: i });
    });
    byView.forEach((ranges, view) => {
      const localCurrent = ranges.findIndex(r => r.globalIndex === current);
      setFindMatches(view, ranges, localCurrent);
    });

    // Same shape for the agenda's rows, which subscribe and paint themselves.
    const byRow = new Map();
    (list || []).forEach((m, i) => {
      if (!m.agendaId) return;
      if (!byRow.has(m.agendaId)) {
        byRow.set(m.agendaId, {
          text: { ranges: [], current: -1 },
          notes: { ranges: [], current: -1 },
        });
      }
      const bucket = byRow.get(m.agendaId)[m.field === 'notes' ? 'notes' : 'text'];
      if (i === current) bucket.current = bucket.ranges.length;
      bucket.ranges.push({ from: m.start, to: m.end });
    });
    publishAgendaFind({ query: '', byRow });
  }, []);

  // Recompute matches against the live list text and re-show the overlay.
  const runSearch = useCallback(() => {
    const m = collectMatches(query);
    setMatches(m);
    setIdx(0);
    setSuspended(false);
    paintCmMatches(m, 0);
    if (m.length) scrollToMatch(m, 0);
  }, [query, scrollToMatch, paintCmMatches]);

  // Recompute matches on query change (and on open). Land on the first hit.
  useEffect(() => {
    if (!open) return;
    runSearch();
  }, [query, open, runSearch]);

  // Suspend the overlay while the user edits a list (offsets would be stale).
  useEffect(() => {
    if (!open) return undefined;
    const onEdit = (e) => {
      const t = e.target;
      if (t && t.matches && t.matches('textarea[data-find-target]')) setSuspended(true);
    };
    document.addEventListener('input', onEdit, true);
    return () => document.removeEventListener('input', onEdit, true);
  }, [open]);

  useEffect(() => {
    if (open) { inputRef.current?.focus(); inputRef.current?.select(); }
    else { setQuery(''); setSuspended(false); paintCmMatches([], -1); clearAgendaFind(); }
  }, [open, paintCmMatches]);

  // Editing a list suspends the overlay because the offsets go stale; the
  // editor's paint has to go with it or it would sit on the wrong words.
  useEffect(() => {
    if (suspended) paintCmMatches([], -1);
  }, [suspended, paintCmMatches]);

  const step = useCallback((dir) => {
    // First key/arrow after editing a list re-runs the search instead of
    // stepping stale offsets.
    if (suspended) { runSearch(); return; }
    if (!matches.length) return;
    const next = (idx + dir + matches.length) % matches.length;
    setIdx(next);
    paintCmMatches(matches, next);
    scrollToMatch(matches, next);
  }, [matches, idx, scrollToMatch, suspended, runSearch, paintCmMatches]);

  if (!open) return null;

  const count = query ? (matches.length ? `${idx + 1}/${matches.length}` : '0/0') : '';
  const noHits = query && matches.length === 0;

  const iconBtn = (label, onClick, title, disabled) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep the input focused
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: 'transparent', border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--text-dimmer)' : 'var(--text-dim)',
        fontFamily: 'var(--mono)', fontSize: 'calc(14px * var(--ui-scale, 1))',
        padding: '4px 8px', lineHeight: 1,
      }}
    >
      {label}
    </button>
  );

  return (
    <>
      <FindOverlay query={query} matches={suspended ? NO_MATCHES : matches} currentIndex={idx} />
      <div style={{
        position: 'fixed',
        top: 'calc(var(--header-h, 48px) + 10px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2350,
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'var(--surface)',
        border: '2px solid var(--accent)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-popover)',
        padding: '7px 8px 7px 14px',
        fontFamily: 'var(--mono)',
      }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="1.6" aria-hidden="true" style={{ flexShrink: 0 }}>
          <circle cx="7" cy="7" r="5" /><path d="M11 11l3.5 3.5" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (suspended) runSearch(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
            else if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
            else if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) { e.preventDefault(); inputRef.current?.select(); }
          }}
          placeholder="find in lists"
          spellCheck={false}
          autoComplete="off"
          style={{
            background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--text)', fontFamily: 'var(--mono)',
            fontSize: 'calc(13px * var(--ui-scale, 1))',
            width: 200,
          }}
        />
        <span style={{
          minWidth: 38, textAlign: 'right',
          color: noHits ? 'var(--danger, #d04a4a)' : 'var(--text-dimmer)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          whiteSpace: 'nowrap',
        }}>
          {count}
        </span>
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '1px 2px' }} />
        {iconBtn('↑', () => step(-1), 'previous (Shift+Enter)', !matches.length)}
        {iconBtn('↓', () => step(1), 'next (Enter)', !matches.length)}
        {iconBtn('✕', () => onClose?.(), 'close (Esc)', false)}
      </div>
    </>
  );
}
